import { makeAutoObservable } from 'mobx'
import type { IDropdownItem, IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowIVRStep } from 'entities/SmartCall/api/types'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowAction,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import { type SmartCallFlowBuilderStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder'
import { isEmptyActions } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/utils/isEmptyActions.ts'

export type IIVRStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowIVRStep['args']
}

export type IBranch = { key: number | 'no_selection'; name: string; nextId: string }
export type IBranchData = Pick<IBranch, 'key' | 'name'>
const branchesKeys = Array(26)
  .fill(null)
  .map((_, i) => i)

export class IVRStore {
  static readonly type = 'ivrPhoneTree' satisfies ISmartCallFlowActionType
  static title = 'IVR phone tree'
  static iconProps: IIconProps = {
    icon: 'dataflow',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore
  readonly greetingMessageStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: IIVRStoreProps,
    private parent: SmartCallFlowBuilderStore
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: IVRStore.type,
      name: IVRStore.title,
      allowedNextActions: [
        'callingFlow',
        'forwardCall',
        'playSound',
        'sendVoicemail',
        'aiTextingAgent',
        'aiCallingAgent',
        'callQueue',
      ],
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'ivrPhoneTree'),
      edgeType: 'customEdgeWithLabel',
    })
    this.greetingMessageStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent.parent,
      args?.greeting_audio_url
        ? {
            audio_url: args?.greeting_audio_url,
          }
        : undefined
    )

    if (args) {
      args.menu_options?.forEach((option) => {
        if (option.key === 'no_selection') return
        this._branchesData.push({
          key: option.key,
          name: option.name,
        })
      })
      this._isReplayGreeting = args.is_replay_greeting ?? false
    }

    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowIVRStep, 'id' | 'type'> = {
    id: IVRStore.type,
    type: 'action',
  }

  get step(): ISmartCallFlowIVRStep {
    return {
      ...this._stepIdType,
      args: {
        is_replay_greeting: this.isReplayGreeting,
        greeting_audio_url: this.greetingMessageStore.args.audio_url,
        menu_options: this.branches.map((branch) => ({
          stepId: branch.nextId,
          key: branch.key,
          name: branch.name,
        })),
      },
      next: this.branches.at(-1)?.nextId || '',
    }
  }

  setApiError = (key: string, message: string) => {}

  checkHasCallActionSteps = (): boolean => {
    this.actionStore.clearNextErrors()
    let valid = true
    this.actionStore.nextIds.slice(0, -1).forEach((id: string) => {
      if (isEmptyActions(id, this.parent?.getActionById)) {
        this.actionStore.setNextError(id)
        valid = false
      }
    })

    return valid
  }

  validate = () => {
    let valid = true
    if (!this.greetingMessageStore.validate()) {
      valid = false
    }
    if (this.branches.length === 1) {
      this.setBranchesError('Add a keypad options to continue')
      valid = false
    }
    return valid
  }

  get isConfigurationError() {
    return !!this.greetingMessageStore.error || !!this.branchesError
  }

  _branchesData: IBranchData[] = []

  get branches(): IBranch[] {
    return this.actionStore.nextIds.map((id, index, self) => {
      if (index === self.length - 1) {
        return {
          key: 'no_selection',
          name: 'No selection',
          nextId: id,
        }
      }
      return {
        key: this._branchesData[index]?.key,
        name: this._branchesData[index]?.name,
        nextId: id,
      }
    })
  }

  private _branchesError = ''
  get branchesError() {
    return this._branchesError
  }

  setBranchesError = (value: string) => {
    this._branchesError = value
  }

  get selectedBranchesKeys() {
    return this._branchesData.map((item) => item.key)
  }

  addBranch = (childId?: string) => {
    this.setBranchesError('')
    const ringAction = this.parent.createAction('callingFlow', {
      parentId: this.actionStore.id,
      childId,
    })
    if (ringAction) {
      this.actionStore.addNextIdBefore(ringAction.actionStore.id)
      this.parent.addAction(ringAction)
      if (!childId) {
        this.parent.appendCallEndsWithAddButton(ringAction.actionStore)
      }

      const sendToVoicemailAction: ISmartCallFlowAction | null = this.parent.createAction(
        'sendVoicemail',
        {
          parentId: ringAction.actionStore.id,
        }
      )
      if (sendToVoicemailAction) {
        ringAction.actionStore.addNextId(sendToVoicemailAction.actionStore.id)
        this.parent.addAction(sendToVoicemailAction)
        this.parent.appendCallEndsWithAddButton(sendToVoicemailAction.actionStore)
      }
    }

    if (ringAction) {
      const key =
        branchesKeys.slice(1).find((key) => !this.selectedBranchesKeys.includes(key)) ||
        (!this.selectedBranchesKeys.includes(0) && 0)
      if (typeof key !== 'number') return
      const newBranch: IBranchData = {
        key,
        name: '',
      }
      this.addBranchData(newBranch)
    }
  }

  get disabledAddBranch() {
    return this.selectedBranchesKeys.length === branchesKeys.length
  }

  addBranchData = (branch: IBranchData) => {
    this._branchesData.push(branch)
  }
  updateBranchData = (branch: IBranchData, index: number) => {
    this._branchesData.splice(index, 1, branch)
  }
  deleteBranch = (branch: IBranch, index: number) => {
    this._branchesData.splice(index, 1)
    this.actionStore.removeNextId(branch.nextId)
    const action = this.parent.getActionById(branch.nextId)
    if (action) this.parent.removeActionAndAllChildren(action)
  }
  getBranchNameByTargetId = (id: string) => {
    const dataIndex = this.actionStore.nextIds.indexOf(id)
    if (dataIndex !== -1) {
      const data = this._branchesData[dataIndex]
      if (data) {
        return `Key ${data.key}${data.name ? ` → ${data.name}` : ''}`
      }
    }
    return 'No selection'
  }

  getBranchKeyOptions = (branch: IBranch): IDropdownItem[] => {
    return branchesKeys
      .filter((key) => branch.key === key || !this.selectedBranchesKeys.includes(key))
      .map((key) => ({
        id: key,
        label: String(key),
      }))
  }

  private _isReplayGreeting = false
  get isReplayGreeting() {
    return this._isReplayGreeting
  }
  setReplayGreeting = (value: boolean) => {
    this._isReplayGreeting = value
  }
}
